import { Auth } from 'aws-amplify';
import React, { Component, Fragment } from 'react';
import { Button, ButtonGroup, UncontrolledTooltip } from 'reactstrap';
import moment from 'moment';
import { CSVLink } from 'react-csv';
import ReactGA from 'react-ga4';
import { Line } from 'react-chartjs-2';
import { toastr } from 'react-redux-toastr';

import { downloadKml, fetchSmelterParamList } from 'actions';
import { StripPlot } from 'components';
import {
	aStripChartStyle,
	rStripChartStyle,
	aStripChartOptions,
	rStripChartOptions
} from '../../screens/Steel/Smelters/chartConfig';

import './StripPlotContainer.scss';

class StripPlotContainer extends Component {
	constructor(props) {
		super(props);
		this.csvActivityLinkRef = React.createRef();

		this.state = {
			csvActivityData: [],
			showDispersionPlot: false,
			canDownload: true
		};
	}

	async componentDidMount() {
		console.log('props', this.props);
		const { title, noDataWarning } = this.props;
		if (noDataWarning) {
			toastr.warning(title, 'No data for chosen date range');
		}
		const authUser = await Auth.currentAuthenticatedUser();
		this.userSub = authUser.attributes['sub'] || '';
	}

	componentDidUpdate(
		{
			alignedCapacityData: prevAlignedCapacityData,
			alignedActivityData: prevAlignedActivityData
		},
		{ csvActivityData: prevCsvActivityData }
	) {
		const { csvActivityData } = this.state;
		const { title, noDataWarning, alignedCapacityData, alignedActivityData } =
			this.props;

		if (csvActivityData.length > prevCsvActivityData.length) {
			this.csvActivityLinkRef.current.link.click();
		}

		if (
			noDataWarning &&
			(prevAlignedActivityData.length !== alignedActivityData.length ||
				prevAlignedCapacityData.length !== alignedCapacityData.length)
		) {
			toastr.warning(title, 'No data for chosen date range');
		}
	}

	downloadParamList = async () => {
		if (this.state.csvActivityData.length) {
			this.csvActivityLinkRef.current.link.click();
			return;
		}
		const { smelterId, title, csvDateFormat } = this.props;
		// API call to get data
		try {
			const response = await fetchSmelterParamList({
				smelterId,
				commodity: 'al'
			});

			const pData = response[title].data.map(
				({ t, v, a, r, hs, lc, mc, hc, ifr, sm, pda, shs, da }) => ({
					t: moment.utc(t).format(csvDateFormat),
					v: v === 1 ? 'on' : 'off',
					a,
					r,
					hs,
					hc,
					mc,
					lc,
					ifr,
					sm,
					pda,
					shs,
					da
				})
			);

			this.setState({ csvActivityData: pData });
		} catch (e) {
			console.log('Error while downloading param list ', e);
		}
	};

	render() {
		const {
			alignedActivityData,
			capacityData,
			alignedCapacityData,
			csvDateFormat,
			idx,
			maxDate,
			newestDate,
			title,
			sandbox,
			startDate,
			endDate,
			isSnapshots
		} = this.props;

		const reducedTitle = title.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
		const kmlButtonId = `kmlDownload${idx}`;
		const csvButtonId = `csvDownload${idx}`;
		const csvActivityButtonId = `csvActivityDownload${idx}`;
		const dispersionPlotButtonId = `dispersionPlotButtonId${idx}`;

		const { csvActivityData, showDispersionPlot } = this.state;

		const stripPlotDatasets = [
			{
				data: alignedActivityData.map(({ t, a }) => {
					return {
						t: moment.utc(t).format('YYYY-MM-DD'),
						y: a
					};
				}),
				...aStripChartStyle
			}
		];

		const stripPlotRawDatasets = [
			{
				data: alignedActivityData.map(({ t, r }) => {
					return {
						t: moment.utc(t).format('YYYY-MM-DD'),
						y: r
					};
				}),
				...rStripChartStyle
			}
		];
		return (
			<div className="mb-2" key={`stripPlot${idx}`}>
				<ButtonGroup className="mb-1 mt-2 pull-right">
					{this.state.canDownload && (
						<Fragment>
							<Button
								id={kmlButtonId}
								color="success"
								size="sm"
								disabled={isSnapshots}
								onClick={() => {
									downloadKml({ commodity: 'al', id: this.props.smelterId });
									ReactGA.event({
										category: 'al_site_kml_download',
										action: 'download',
										label: this.userSub
									});
									return true;
								}}
							>
								<i className="fa fa-globe" aria-hidden="true" />
								<span className="sr-only">Download kml</span>
							</Button>
							<UncontrolledTooltip
								placement="top"
								delay={500}
								target={kmlButtonId}
								trigger="click hover"
							>
								<i className="fa fa-download mr-2" aria-hidden="true" />{' '}
								Download this data in KML format.
							</UncontrolledTooltip>
							<Fragment>
								<Button
									id={csvActivityButtonId}
									color="success"
									size="sm"
									onClick={this.downloadParamList}
								>
									<i className="fa fa-line-chart" aria-hidden="true" />
								</Button>
								<CSVLink
									ref={this.csvActivityLinkRef}
									data={csvActivityData}
									headers={[
										{ label: 'Date', key: 't' },
										{ label: 'State', key: 'v' },
										{ label: 'Activity dispersion', key: 'a' },
										{ label: 'Raw index score', key: 'r' },
										{ label: 'Hotspots', key: 'hs' },
										{ label: 'Low count', key: 'lc' },
										{ label: 'Medium count', key: 'mc' },
										{ label: 'High count', key: 'hc' },
										{ label: 'Cloud pct', key: 'ifr' },
										{ label: 'Smoke', key: 'sm' },
										{ label: 'Data age (prime)', key: 'pda' },
										{ label: 'Secondary hotspot', key: 'shs' },
										{ label: 'Data age (prime + secondary)', key: 'da' }
									]}
									filename={`${title}-activity-dispersion-and-raw-index-score-data.csv`}
									className="d-none"
									onClick={() => {
										ReactGA.event({
											category: 'smeltersMap',
											action:
												'activity dispersion and raw index score data downloaded',
											label: `${title}-activity-dispersion-and-raw-index-score-data-and-related-data.csv`
										});
										return true;
									}}
								/>
							</Fragment>
							<UncontrolledTooltip
								placement="top"
								delay={500}
								target={csvActivityButtonId}
								trigger="click hover"
							>
								<i className="fa fa-download mr-2" aria-hidden="true" />{' '}
								Download Activity Dispersion, Raw Index Score and related data
								in CSV format.
							</UncontrolledTooltip>
							<CSVLink
								id={csvButtonId}
								filename={`${title}-inactive-capacity-data.csv`}
								headers={[
									{ label: 'Capture time', key: 't' },
									{ label: 'State', key: 'v' }
								]}
								data={capacityData.map(({ t, v }) => {
									let dspState = 'unknown';
									if (v === 1) {
										dspState = 'on';
									} else if (v === 0) {
										dspState = 'off';
									}
									return {
										t: moment.utc(t).format(csvDateFormat),
										v: dspState
									};
								})}
								className="btn btn-success btn-sm"
								onClick={() => {
									ReactGA.event({
										category: 'smeltersMap',
										action: 'inactive capacity data downloaded',
										label: `${title}-inactive-capacity-data.csv`
									});
									return true;
								}}
							>
								<i className="fa fa-table" aria-hidden="true" />
							</CSVLink>
							<UncontrolledTooltip
								placement="top"
								delay={500}
								target={csvButtonId}
								trigger="click hover"
							>
								<i className="fa fa-download mr-2" aria-hidden="true" />{' '}
								Download Inactive Capacity Index data in CSV format.
							</UncontrolledTooltip>
						</Fragment>
					)}
					<Button
						id={dispersionPlotButtonId}
						color={showDispersionPlot ? 'secondary' : 'success'}
						size="sm"
						onClick={() =>
							this.setState({ showDispersionPlot: !showDispersionPlot })
						}
					>
						<i className="fa fa-bar-chart" aria-hidden="true" />
						<span className="sr-only">
							Toggle Activity Dispersion Index plot
						</span>
					</Button>
					<UncontrolledTooltip
						placement="top"
						delay={500}
						target={dispersionPlotButtonId}
						trigger="click hover"
					>
						<i className="fa fa-eye-slash" aria-hidden="true" /> Toggle Activity
						Dispersion plot visibility
					</UncontrolledTooltip>
				</ButtonGroup>
				<div>
					<h6 className="mb-0 mt-3 d-inline-block">
						{sandbox ? (
							<Fragment>
								{title}
								<span className="asterisk">*</span>
							</Fragment>
						) : (
							title
						)}
					</h6>{' '}
					&nbsp;&nbsp;&nbsp;
					<span className="index-dates">
						{startDate ? `First date in index: ${startDate}` : ''}
					</span>
					<span className="index-dates">
						{endDate ? `, Last date in index: ${endDate}` : ''}
					</span>
				</div>
				{showDispersionPlot && (
					<div className="chart-container">
						<Line
							data={{ datasets: stripPlotDatasets }}
							options={aStripChartOptions}
						/>
					</div>
				)}
				<div className="chart-container">
					<Line
						data={{ datasets: stripPlotRawDatasets }}
						options={rStripChartOptions}
					/>
				</div>
				<StripPlot
					id={reducedTitle}
					data={alignedCapacityData}
					maxDate={maxDate}
					newestDate={newestDate}
					minWidth={700}
					plotHeight={40}
					axisHeight={40}
					tickHeight={5}
					hoverHeight={4}
					margin={{
						left: 5,
						top: 0,
						right: 5,
						bottom: 0
					}}
				/>
			</div>
		);
	}
}

export default StripPlotContainer;
